import { ReactElement, useMemo, useState } from 'react';
import styles from './user-email-account-form.module.scss';
import { Input } from '@utmb/design-system/input/Input';
import { Button } from '@utmb/design-system/button/Button';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { useUser } from '@utmb/contexts/AuthContext';
import { FetchMethodsEnum, fetchTheAPI } from '@utmb/utils/utmb';
import { Font } from '@utmb/design-system/text/font';
import DashboardRemovalUpdateStatusView from '@utmb/types/view/DashboardRemovalUpdateStatusView';
import { Skeleton } from '@utmb/design-system/placeholder/Placeholder';
import { useTranslation } from 'next-i18next';
import { DashboardRemovalUpdateStatusEnum } from '@utmb/types/enum/DashboardRemovalUpdateStatusEnum';

export interface UserEmailAcountFormProps {
    closeCallback: () => void;
}

export const UserEmailAcountForm = ({ closeCallback }: UserEmailAcountFormProps): ReactElement => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [response, setResponse] = useState<DashboardRemovalUpdateStatusView>();
    const user = useUser();
    const [isCancel, setIsCancel] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    const message = useMemo(() => {
        if (response) {
            if (response.statusCode === DashboardRemovalUpdateStatusEnum.EMAIL) {
                return t('myUtmb.email.response.email', { email: email });
            } else if (response.statusCode === DashboardRemovalUpdateStatusEnum.HUMAN) {
                return t('myUtmb.email.response.human', { email: email });
            } else if (response.statusCode === DashboardRemovalUpdateStatusEnum.REQUESTED) {
                return t('myUtmb.email.response.requested', { email: email });
            } else {
                return t('myUtmb.email.response.error');
            }
        } else {
            return null;
        }
    }, [response]);

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
        setEmail(event.target.value);
    }

    async function handleConfirmeClick(): Promise<void> {
        if (!isLoading) {
            setIsLoading(true);
            const token = await user.getToken();

            fetchTheAPI({
                isClient: true,
                method: FetchMethodsEnum.POST,
                path: `/users/dashboard/member/change-email-request`,
                bearer: token,
                body: { email: email },
            })
                .then((responseData) => {
                    setResponse(responseData as DashboardRemovalUpdateStatusView);
                    setIsLoading(false);
                })
                .catch((error) => {
                    alert(error);
                    setIsLoading(false);
                });
        }
    }

    async function handleCancelClick(): Promise<void> {
        if (!isLoading) {
            setIsLoading(true);
            const token = await user.getToken();

            fetchTheAPI({
                isClient: true,
                method: FetchMethodsEnum.DELETE,
                path: `/users/dashboard/member/cancel-email-request`,
                bearer: token,
                body: { email: email },
            })
                .then(() => {
                    setIsCancel(true);
                    setIsLoading(false);
                })
                .catch((error) => {
                    alert(error);
                    setIsLoading(false);
                });
        }
    }

    return (
        <div className={styles.email_account_form}>
            <div className={styles.wrap_input}>
                {(!response || response.error) && !isLoading ? (
                    <Input
                        id="email"
                        name="email"
                        type="text"
                        placeholder={t('myUtmb.email.placeholder')}
                        onchange={handleChange}
                        onblur={handleChange}
                        value={email}
                    />
                ) : null}

                {isLoading ? <Skeleton width="100%" height="60" /> : null}

                {message && !isCancel ? (
                    <Font.FuturaBook mobile="22" desktop="22" as="span">
                        {message}
                    </Font.FuturaBook>
                ) : null}

                {isCancel ? (
                    <Font.FuturaBook mobile="22" desktop="22" as="span">
                        {t('myUtmb.email.response.cancelled')}
                    </Font.FuturaBook>
                ) : null}
            </div>
            <div className={styles.wrap_button}>
                <Button.Default
                    className={styles.button}
                    color={getColorStyle(ColorTypesEnum.WHITE)}
                    bgcolor={getColorStyle(ColorTypesEnum.DEFAULT)}
                    type="button"
                    label={t('myUtmb.email.button.close')}
                    onPress={closeCallback}
                />
                {!response || response.error ? (
                    <Button.Default
                        className={styles.button}
                        disabled={email !== '' && !isLoading ? false : true}
                        color={getColorStyle(ColorTypesEnum.WHITE)}
                        bgcolor={getColorStyle(ColorTypesEnum.PRIMARY)}
                        type="button"
                        label={t('myUtmb.email.button.confirm')}
                        onPress={handleConfirmeClick}
                    />
                ) : (
                    <>
                        {!isCancel ? (
                            <Button.Default
                                className={styles.button}
                                color={getColorStyle(ColorTypesEnum.WHITE)}
                                bgcolor={getColorStyle(ColorTypesEnum.RED)}
                                type="button"
                                label={t('myUtmb.email.button.cancel')}
                                disabled={isLoading ? true : false}
                                onPress={handleCancelClick}
                            />
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
};
